import {Component, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DataService} from '../services/dataService';
import {Subscription} from 'rxjs';
import {CordovaUIService, SynchroStreamInfo, SynchroStreamStatus} from '../../common';
import {PageEnum} from '../entity';
import {UserPreferences} from '../entity';

@Component({
    selector   : 'app-setting',
    templateUrl: './setting.component.html',
    styleUrls  : ['./setting.component.scss']
})
export class SettingComponent implements OnInit, OnDestroy {
    /**
     * Is Syncing.
     */
    private _isSyncing: boolean = false;
    /**
     * Is Auto Sync
     */
    private _isAutoSync: boolean = false;
    /**
     * Subscription Sync Info Stream
     */
    private syncInfoSubscription: Subscription;

    /**
     * Items Number Total
     */
    private _itemsNumberTotal: number;
    /**
     * Items Number Done
     */
    private _itemsNumberDone: number;
    /**
     * Items Number Percentage
     */
    private _itemsNumberPercentage: number;
    /**
     * Items Size Total
     */
    private _itemsSizeTotal: number;
    /**
     * Items Size Done
     */
    private _itemsSizeDone: number;
    /**
     * Items Size Percentage
     */
    private _itemsSizePercentage: number;
    /**
     * Current Item Number Total
     */
    private _currentItemSizeTotal: number;
    /**
     * Current Item Number Done
     */
    private _currentItemSizeDone: number;
    /**
     * Current Item Size Percentage
     */
    private _currentItemSizePercentage: number;
    /**
     * Show Items number
     */
    private _showItemsNumber: boolean = false;
    /**
     * Show Items Size
     */
    private _showItemsSize: boolean = false;
    /**
     * Show Current Items Size
     */
    private _showCurrentItemSize: boolean = false;
    /**
     * Label Synchro Status.
     */
    private _labelSynchroStatus: string = '';
    /**
     * Label Synchro Errors
     */
    private _labelSynchroErrors: Array<string> = [];
    /**
     * Is Logged ?
     */
    private _isLogin: boolean = false;
    /**
     * User Filter.
     */
    private _userPreferences: UserPreferences = null;

    /**
     * Constructor
     * @param translate
     * @param dataService
     * @param cordovaService
     */
    constructor(protected translate: TranslateService,
                protected dataService: DataService,
                private cordovaService: CordovaUIService) {
    }


    /**
     * NgOnInit
     */
    public ngOnInit() {
        console.log('OnInit SettingComponent');
        this.dataService.cleanNewPage();
        this.translate.get('settingPage.pageTitle').subscribe(
            (res: string) => {
                this.dataService.publishNewPageTitle(PageEnum.SETTING, res);
            }
        );
        this.dataService.isReadyToStart().then(() => {
            this.syncInfoSubscription = this.dataService.getInfoStream()
                .subscribe(this.computeSyncInfoStream.bind(this));
            this._isSyncing = this.dataService.isSyncing();
            this._isAutoSync = this.dataService.isAutoSync();
            this._isLogin = this.dataService.isLogged;
            this._userPreferences = {
                ponderationUtilisation1: this.dataService.userPreferences.ponderationUtilisation1,
                ponderationUtilisation2: this.dataService.userPreferences.ponderationUtilisation2,
                lang                   : this.dataService.userPreferences.lang,
            };
        });
    }


    /**
     * OnDestroy
     */
    public ngOnDestroy(): void {
        if (this.syncInfoSubscription != null) {
            this.syncInfoSubscription.unsubscribe();
        }
    }


    /**
     * Compute the info stream.
     * @param info
     */
    private computeSyncInfoStream(info: SynchroStreamInfo) {
        console.log('>>>>>', info);
        if (info != null) {
            switch (info.status) {
                case SynchroStreamStatus.CONNECTIVITY_OK:
                case SynchroStreamStatus.CONNECTIVITY_KO:
                    break;
                case SynchroStreamStatus.START_SYNCHRO:
                    this._isSyncing = true;
                    this._labelSynchroErrors = [];
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.START_SYNCHRO');
                    break;
                case SynchroStreamStatus.ALREADY_SYNCING:
                    // this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ALREADY_SYNCING'));
                    break;
                case SynchroStreamStatus.SEND_DATA:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.SEND_DATA');
                    break;
                case SynchroStreamStatus.SEND_DATA_INFO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.SEND_DATA_INFO');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = false;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.SEND_FILE:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.SEND_FILE');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.SEND_FILE_INFO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.SEND_FILE_INFO');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.SEND_FILE_INFO_PROGRESS:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.SEND_FILE_INFO_PROGRESS');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._currentItemSizeTotal = info.currentItemSizeTotal;
                    this._currentItemSizeDone = info.currentItemSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._currentItemSizePercentage = this._currentItemSizeDone * 100 / this._currentItemSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = true;
                    break;
                case SynchroStreamStatus.GET_DATA:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.GET_DATA');
                    break;
                case SynchroStreamStatus.GET_DATA_INFO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.GET_DATA_INFO');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = false;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.GET_FILE:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.GET_FILE');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.GET_FILE_INFO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.GET_FILE_INFO');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.GET_FILE_INFO_PROGRESS:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.GET_FILE_INFO_PROGRESS');
                    this._itemsNumberTotal = info.itemsNumberTotal;
                    this._itemsNumberDone = info.itemsNumberDone;
                    this._itemsSizeTotal = info.itemsSizeTotal;
                    this._itemsSizeDone = info.itemsSizeDone;
                    this._currentItemSizeTotal = info.currentItemSizeTotal;
                    this._currentItemSizeDone = info.currentItemSizeDone;
                    this._itemsNumberPercentage = this._itemsNumberDone * 100 / this._itemsNumberTotal;
                    this._itemsSizePercentage = this._itemsSizeDone * 100 / this._itemsSizeTotal;
                    this._currentItemSizePercentage = this._currentItemSizeDone * 100 / this._currentItemSizeTotal;
                    this._showItemsNumber = true;
                    this._showItemsSize = true;
                    this._showCurrentItemSize = true;
                    break;
                case SynchroStreamStatus.ERROR_DEXIE:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_DEXIE'));
                    break;
                case SynchroStreamStatus.ERROR_CORDOVA_FILETRANSFER_GET:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_CORDOVA_FILETRANSFER_GET'));
                    break;
                case SynchroStreamStatus.ERROR_CORDOVA_FILETRANSFER_POST:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_CORDOVA_FILETRANSFER_POST'));
                    break;
                case SynchroStreamStatus.ERROR_CORDOVA_FILE_SAVE:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_CORDOVA_FILE_SAVE'));
                    break;
                case SynchroStreamStatus.ERROR_HTTP_GET:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_HTTP_GET'));
                    break;
                case SynchroStreamStatus.ERROR_HTTP_POST:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_HTTP_POST'));
                    break;
                case SynchroStreamStatus.ERROR_MPA:
                    this._labelSynchroErrors.push(this.translate.instant('settingPage.synchroStatus.ERROR_MPA'));
                    break;
                case SynchroStreamStatus.END_NORMAL_SYNCHRO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.END_NORMAL_SYNCHRO');
                    this._isSyncing = false;
                    this._showItemsNumber = false;
                    this._showItemsSize = false;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.END_BY_ERROR_SYNCHRO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.END_BY_ERROR_SYNCHRO');
                    this._isSyncing = false;
                    this._showItemsNumber = false;
                    this._showItemsSize = false;
                    this._showCurrentItemSize = false;
                    break;
                case SynchroStreamStatus.END_BY_STOP_SYNCHRO:
                    this._labelSynchroStatus = this.translate.instant('settingPage.synchroStatus.END_BY_STOP_SYNCHRO');
                    this._isSyncing = false;
                    this._showItemsNumber = false;
                    this._showItemsSize = false;
                    this._showCurrentItemSize = false;
                    break;
            }
        }
    }

    /**
     * Stop the current Synchro
     */
    public stopSynchro(): void {
        this.dataService.stopSynchro();
    }

    /**
     * Start the current Synchro
     */
    public startSynchro(): void {
        this.dataService.startSynchro();
    }

    /**
     * Start or Stop Auto Sync.
     */
    public startOrStopAutoSync(): void {
        if (this.dataService.isAutoSync()) {
            this.dataService.stopAutoSync();
            this._isAutoSync = false;
        } else {
            this.dataService.startAutoSync();
            this._isAutoSync = true;
        }
    }


    /**
     * Getter Is Syncing
     */
    public get isSyncing(): boolean {
        return this._isSyncing;
    }

    /**
     * Getter Is Auto Syncing
     */
    public get isAutoSync(): boolean {
        return this._isAutoSync;
    }

    /**
     * Getter.
     */
    public get showItemsNumber(): boolean {
        return this._showItemsNumber;
    }

    /**
     * Getter.
     */
    public get showItemsSize(): boolean {
        return this._showItemsSize;
    }

    /**
     * Getter.
     */
    public get showCurrentItemSize(): boolean {
        return this._showCurrentItemSize;
    }

    /**
     * Getter.
     */
    public get currentItemSizeTotal(): number {
        return this._currentItemSizeTotal;
    }

    /**
     * Getter.
     */
    public get currentItemSizeDone(): number {
        return this._currentItemSizeDone;
    }

    /**
     * Getter.
     */
    public get currentItemSizePercentage(): number {
        return this._currentItemSizePercentage;
    }


    /**
     * Getter.
     */
    public get itemsNumberTotal(): number {
        return this._itemsNumberTotal;
    }

    /**
     * Getter.
     */
    public get itemsNumberDone(): number {
        return this._itemsNumberDone;
    }

    /**
     * Getter.
     */
    public get itemsNumberPercentage(): number {
        return this._itemsNumberPercentage;
    }

    /**
     * Getter.
     */
    public get itemsSizeTotal(): number {
        return this._itemsSizeTotal;
    }

    /**
     * Getter.
     */
    public get itemsSizeDone(): number {
        return this._itemsSizeDone;
    }

    /**
     * Getter.
     */
    public get itemsSizePercentage(): number {
        return this._itemsSizePercentage;
    }


    /**
     * Getter.
     */
    public get labelSynchroStatus(): string {
        return this._labelSynchroStatus;
    }

    /**
     * Getter.
     */
    public get labelSynchroErrors(): Array<string> {
        return this._labelSynchroErrors;
    }

    /**
     * Getter.
     */
    public get isLogin(): boolean {
        return this._isLogin;
    }


    /**
     * Save setting.
     */
    public saveSetting(): void {
        this.dataService.saveUserPreferences(this._userPreferences)
            .then(
                () => {
                    this.cordovaService.toastSuccess(
                        this.translate.instant('settingPage.saveSuccess'),
                        this.translate.instant('settingPage.info'),
                    );
                },
                () => {
                    this.cordovaService.dialogAlert(
                        this.translate.instant('settingPage.error'),
                        this.translate.instant('settingPage.error')
                    ).catch();
                });
    }

    /**
     * Getter User Preference
     */
    public get userPreferences(): UserPreferences {
        return this._userPreferences;
    }
}
